import { assertExists } from '@blocksuite/global/utils';
import type { BlockElement } from '@blocksuite/lit';

import type { OnDragEndProps } from '../../page-block/widgets/drag-handle/config.js';
import type { EdgelessBlockType } from '../../surface-block/index.js';
import { Bound } from '../../surface-block/index.js';
import { getBlockProps, getEdgelessPage, isPageMode } from './query.js';

function buildViewportKey(pageId: string) {
  return 'blocksuite:' + pageId + ':edgelessViewport';
}

type ViewportData =
  | {
      x: number;
      y: number;
      zoom: number;
    }
  | {
      /**
       * The id of the block that the viewport is centered on.
       */
      referenceId: string;
      padding?: [number, number, number, number];
    };

export function saveViewportToSession(pageId: string, viewport: ViewportData) {
  sessionStorage.setItem(buildViewportKey(pageId), JSON.stringify(viewport));
}

export function getViewportFromSession(pageId: string): ViewportData | null {
  try {
    const storedViewport = sessionStorage.getItem(buildViewportKey(pageId));
    return storedViewport ? JSON.parse(storedViewport) : null;
  } catch {
    return null;
  }
}

export function convertBlockDocToEdgeless({
  blockComponent,
  dragPreview,
  cssSelector,
  width,
  height,
}: OnDragEndProps & {
  blockComponent: BlockElement;
  cssSelector: string;
  width?: number;
  height?: number;
}): boolean {
  const page = blockComponent.page;
  if (isPageMode(page)) return false;
  const edgelessPage = getEdgelessPage(page);
  assertExists(edgelessPage);

  const previewEl = dragPreview.querySelector(cssSelector);
  assertExists(previewEl);
  const rect = previewEl.getBoundingClientRect();
  const point = edgelessPage.surface.toModelCoord(rect.x, rect.y);
  const bound = new Bound(
    point[0],
    point[1],
    width ?? previewEl.clientWidth,
    height ?? previewEl.clientHeight
  );

  const blockModel = blockComponent.model;
  const blockProps = getBlockProps(blockModel);

  edgelessPage.surface.addElement(
    blockComponent.flavour as EdgelessBlockType,
    {
      ...blockProps,
      xywh: bound.serialize(),
    },
    edgelessPage.surface.model
  );
  page.deleteBlock(blockModel);
  return true;
}

export function convertBlockEdgelessToDoc({
  blockComponent,
  dropBlockId,
  dropType,
}: OnDragEndProps & {
  blockComponent: BlockElement;
}): boolean {
  const page = blockComponent.page;
  const targetBlock = page.getBlockById(dropBlockId);
  if (!targetBlock) return false;

  const shouldInsertIn = dropType === 'in';
  const parentBlock = shouldInsertIn
    ? targetBlock
    : page.getParent(targetBlock);
  assertExists(parentBlock);
  const parentIndex = shouldInsertIn
    ? 0
    : parentBlock.children.indexOf(targetBlock);

  const blockModel = blockComponent.model;
  const blockProps = getBlockProps(blockModel);
  delete blockProps.width;
  delete blockProps.height;
  delete blockProps.xywh;
  delete blockProps.zIndex;

  page.addBlock(blockModel.flavour, blockProps, parentBlock, parentIndex);
  page.deleteBlock(blockModel);
  return true;
}
